// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { Button, Markdown, StatusMessage, useTranslate, copyToClipboard } from '@plesk/ui-library';
import type { Tokens } from 'marked';
import React, { useEffect, useRef } from 'react';
import { Answer } from '../../CopilotContext';
import PopoverConfirm from './PopoverConfirm';
import { addToast } from 'helpers/toasts';
import { escapeHtml } from 'jsw';

type MessageItemProps = {
    message: Answer;
    onRemove: (id: Answer['id']) => void;
};

const MessageItem: React.FC<MessageItemProps> = ({ message, onRemove }) => {
    const translate = useTranslate('components.copilot');

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const copyButton = target.closest('.code-copy-icon');
            if (copyButton) {
                const code = copyButton.parentElement?.querySelector('code')?.textContent;
                if (code) {
                    try {
                        copyToClipboard(code);
                        addToast({
                            intent: 'success',
                            message: translate('copy-snippet-success') as string,
                        });
                    } catch {
                        addToast({
                            intent: 'danger',
                            message: translate('copy-snippet-error') as string,
                        });
                    }
                }
            }
        };
        const node = contentRef.current;
        node?.addEventListener('click', handler);
        return () => node?.removeEventListener('click', handler);
    }, [translate]);

    return (
        <div
            key={message.id}
            className={`plesk-copilot-msg-row ${message.role === 'user' ? 'right' : 'left'}`}
        >
            {message.role === 'assistant' ? (
                <div className="plesk-copilot-assistant-card">
                    <div className="assistant-content" ref={contentRef}>
                        {message.error ? (
                            <StatusMessage intent="danger">{message.error}</StatusMessage>
                        ) : (
                            <Markdown parserOptions={{
                                extensions: {
                                    renderers: {
                                        link: token => {
                                            const t = token as Tokens.Link;
                                            let linkUrl: URL | null = null;
                                            let isSameDomain = false;
                                            try {
                                                linkUrl = new URL(t.href);
                                                const currentUrl = new URL(window.location.href);
                                                isSameDomain = linkUrl.hostname === currentUrl.hostname;
                                                return `<a href="${t.href}" title="${t.title || ''}" ${isSameDomain ? '' : 'target="_blank" rel="noopener noreferrer"'}>
                                                            ${t.text}</a>`;
                                            } catch {
                                                return `<span>${t.text}</span>`;
                                            }
                                        },
                                        code: token => `<div class="pre-code-wrapper"><pre><code>${escapeHtml(token.text)}</code>` +
                                            `<button class="code-copy-icon pul-button pul-button--ghost" type="button" aria-label="${translate('copy-snippet-label')}">` +
                                            '<span class="pul-button__inner">' +
                                            '<span class="pul-icon pul-icon--size-12 pul-button__icon" aria-hidden="true">' +
                                            '<svg focusable="false"><use href="/cp/theme/icons/24/colored-icons-sprite.svg#copy"></use></svg></span>' +
                                            '<span>Copy</span></span></button></pre></div>',
                                    },
                                    childTokens: {},
                                },

                            }}
                            >
                                {message.content ?? ''}
                            </Markdown>
                        )}
                    </div>
                    <PopoverConfirm
                        target={(
                            <Button
                                icon="recycle"
                                ghost
                                tooltip={translate('remove-pair-message')}
                                onClick={() => onRemove(message.id)}
                            />)}
                    >
                        {translate('confirm-remove-pair-message-question')}
                    </PopoverConfirm>
                </div>
            ) : (
                <div className="user-chip">
                    <span>{message.content}</span>
                </div>
            )}
        </div>
    );
};

export default MessageItem;
